<template>
  <v-card>
    <div class="d-flex justify-space-between align-center">
      <v-card-title class="text-subtitle-1">{{
        $vuetify.lang.t('$vuetify.BOOKING.OCCUPANCY_CALENDAR')
      }}</v-card-title>
      <!-- start date -->
      <v-col cols="12" sm="3" class="d-flex justify-end align-center">
        <v-menu
          v-if="$vuetify.breakpoint.mdAndUp"
          v-model="bookingsHeatmapStartTimeDatePicker"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="formattedBookingsHeatmapStartTimeDate"
              :label="$vuetify.lang.t('$vuetify.BOOKING.START_DATE')"
              :placeholder="$vuetify.lang.t('$vuetify.BOOKING.START_DATE')"
              append-icon="mdi-calendar"
              :hide-details="hideDateFormatError"
              outlined
              v-bind="attrs"
              autocomplete="off"
              :rules="dateFormatRule"
              v-on="on"
            ></v-text-field>
            <v-btn icon class="ml-2" @click="onRefreshClick()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <v-date-picker
            v-model="bookingsHeatmapStartTimeDatePart"
            :first-day-of-week="1"
            @input="onDateChange"
          ></v-date-picker>
        </v-menu>
        <v-dialog
          v-if="$vuetify.breakpoint.smAndDown"
          v-model="bookingsHeatmapStartTimeDatePicker"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="formattedBookingsHeatmapStartTimeDate"
              :label="$vuetify.lang.t('$vuetify.BOOKING.START_DATE')"
              :placeholder="$vuetify.lang.t('$vuetify.BOOKING.START_DATE')"
              append-icon="mdi-calendar"
              outlined
              :hide-details="hideDateFormatError"
              clearable
              v-bind="attrs"
              :rules="dateFormatRule"
              autocomplete="off"
              @click:clear="startTimeDate = null"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="bookingsHeatmapStartTimeDatePart"
            :first-day-of-week="1"
            @input="bookingsHeatmapStartTimeDatePicker = false"
          ></v-date-picker>
        </v-dialog>
      </v-col>
    </div>
    <v-card-text>
      <div
        v-if="
          loading.state && loading.type === 'getBookablePlaceBookingsHeatmap'
        "
        class="d-flex align-center justify-center py-4"
        style="min-height: 350px;"
      >
        <v-progress-circular
          indeterminate
          color="secondary"
        ></v-progress-circular>
      </div>
      <div v-else>
        <apexchart
          ref="chart"
          type="heatmap"
          height="350"
          :options="chartOptions"
          :series="heatmapData"
        ></apexchart>
      </div>
    </v-card-text>
  </v-card>
</template>
<script>
import formatDate from '@/mixins/formatDate'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'BookingsHeatChart',
  mixins: [formatDate],
  data() {
    return {
      selectedDate: '',
      maxParkingSpots: undefined,
      chartOptions: {
        chart: {
          height: 350,
          type: 'heatmap'
        },
        dataLabels: {
          enabled: true,
          style: {
            colors: ['#333']
          }
        },
        colors: ['#c8697e'],
        title: {
          text: ''
        },
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.5,
            radius: 0,
            useFillColorAsStroke: false,
            colorScale: {
              min: 0,
              max: undefined
            }
          }
        }
      },
      bookingsHeatmapStartTimeDatePicker: false,
      bookingsHeatmapStartTimeDatePart: '',
      dateFormatRule: [
        v =>
          /^\d{2}\.\d{2}\.\d{4}$/.test(v) ||
          this.$vuetify.lang.t('$vuetify.BOOKING.DATE_FORMAT_INCORRECT')
      ],
      heatmapData: []
    }
  },
  computed: {
    ...mapGetters('booking', [
      'selectedBookingProfileId',
      'selectedBookingProfile'
    ]),
    ...mapGetters('app', ['loading']),
    formattedBookingsHeatmapStartTimeDate: {
      get() {
        if (this.bookingsHeatmapStartTimeDatePart) {
          const parts = this.bookingsHeatmapStartTimeDatePart.split('-')
          return `${parts[2]}.${parts[1]}.${parts[0]}`
        }
        return null
      },
      set(value) {
        try {
          const parts = value.split('.')
          if (Array.isArray(parts) && parts.length === 3) {
            const day = parts[0]
            const month = parts[1]
            const year = parts[2]
            this.bookingsHeatmapStartTimeDatePart = `${year}-${month}-${day}`
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log(err)
        }
      }
    },
    hideDateFormatError() {
      if (
        /^\d{2}\.\d{2}\.\d{4}$/.test(this.formattedBookingsHeatmapStartTimeDate)
      ) {
        return true
      }
      if (!this.formattedBookingsHeatmapStartTimeDate) {
        return true
      }
      return false
    }
  },
  watch: {
    selectedBookingProfileId(newVal, oldVal) {
      if (newVal !== oldVal || oldVal === undefined) {
        this.getHeatmapData()
      }
    },
    maxParkingSpots(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (this.$refs.chart) {
          this.$refs.chart.updateOptions({
            plotOptions: {
              heatmap: {
                colorScale: {
                  max: this.maxParkingSpots
                }
              }
            }
          })
        }
      }
    }
  },
  created() {
    const dt = new Date()
    const day = String(dt.getDate()).padStart(2, '0')
    const month = String(dt.getMonth() + 1).padStart(2, '0')
    const year = dt.getFullYear()
    this.bookingsHeatmapStartTimeDatePart = `${year}-${month}-${day}`
    this.getHeatmapData()
  },
  methods: {
    ...mapActions('booking', ['getBookablePlaceBookingsHeatmap']),
    onRefreshClick() {
      this.getHeatmapData()
    },
    getHeatmapData() {
      if (!this.selectedBookingProfile) {
        return
      }
      this.getBookablePlaceBookingsHeatmap({
        this: this,
        bookingProfileId: this.selectedBookingProfileId,
        startTime: this.bookingsHeatmapStartTimeDatePart
      }).then(data => {
        this.heatmapData = data.Data.reverse()
        this.maxParkingSpots = data.MaxNumberOfSpots
      })
    },

    onDateChange(newVal, oldVal) {
      if (newVal !== oldVal || oldVal === undefined) {
        this.getHeatmapData()
      }
      this.bookingsHeatmapStartTimeDatePicker = false
    }
  }
}
</script>

<style scoped>
::v-deep .apexcharts-toolbar {
  z-index: 1 !important;
}
</style>
